import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { ToastController } from '@ionic/angular';
import { select, Store } from '@ngrx/store';

import {
  cloneDeep,
  compact,
  findIndex,
  includes,
  isNil,
  isUndefined,
  lowerCase,
  map as lodashMap,
  map,
  orderBy,
  sortBy
} from 'lodash-es';
import { of, throttleTime } from 'rxjs';
import { concatMap, distinctUntilChanged, filter } from 'rxjs/operators';

import { defaultToastDuration } from '../../../../../constants/toast.constants';
import { walkingFarmAvailableLeadStatuses } from '../../../../../constants/walking-farm.constant';
import {
  WalkingFarmLeadStatusEnum,
  WalkingFarmPropertiesOwnerStatusFilterEnum,
  WalkingFarmPropertiesSortByEnum
} from '../../../../../enums/walking-farm';
import * as fromWalkingFarmActions from '../../../../../store/actions/walking-farm.actions';
import * as fromWalkingFarmAcions from '../../../../../store/actions/walking-farm.actions';
import * as fromWalkingFarmSelectors from '../../../../../store/selectors/walking-farm.selectors';
import { WalkingFarmProperty } from '../../../../../typings/walking-farm';
import { TooltipService } from '../../../../services/tooltip.service';

interface FiltersFormGroup {
  filterByLeadStatus: FormArray<FormControl<boolean | null>>;
  filterByOwnerStatus: FormControl<WalkingFarmPropertiesOwnerStatusFilterEnum | null>;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'red-walking-farm-view-filter',
  standalone: false,
  templateUrl: './filter.component.html'
})
export class FilterPageComponent {
  private store = inject(Store);
  private activatedRoute = inject(ActivatedRoute);
  private toastController = inject(ToastController);
  private tooltipService = inject(TooltipService);

  walkingFarmId: string | null = null;

  filterForm: FormGroup<FiltersFormGroup> = new FormGroup<FiltersFormGroup>({
    filterByLeadStatus: new FormArray(walkingFarmAvailableLeadStatuses.map(() => new FormControl<boolean>(false))),
    filterByOwnerStatus: new FormControl<WalkingFarmPropertiesOwnerStatusFilterEnum>(
      WalkingFarmPropertiesOwnerStatusFilterEnum.All
    )
  });

  readonly availableLeadStatuses = walkingFarmAvailableLeadStatuses;
  readonly ownerOccupiedFilter = WalkingFarmPropertiesOwnerStatusFilterEnum;
  readonly walkingFarmPropertiesSortBy = WalkingFarmPropertiesSortByEnum;

  properties: WalkingFarmProperty[] = [];

  defaultBackHref: string | undefined;
  defaultHref: string;

  constructor() {
    this.walkingFarmId = this.activatedRoute.snapshot.paramMap.get('id');

    this.defaultBackHref = this.activatedRoute.snapshot.data['defaultBackHref'];
    this.defaultHref = [this.defaultBackHref ?? '', 'walking-farm', this.walkingFarmId, 'view'].join('/');

    this.filterForm.valueChanges
      .pipe(throttleTime(500, undefined, { leading: true, trailing: true }), distinctUntilChanged())
      .subscribe((values) => {
        if (this.walkingFarmId) {
          this.store.dispatch(
            fromWalkingFarmActions.updateViewFarmFilters({
              payload: {
                farmId: this.walkingFarmId,
                filterByLeadStatus: compact(
                  map(walkingFarmAvailableLeadStatuses, (item, index) =>
                    values.filterByLeadStatus && values.filterByLeadStatus[index] ? item.value : null
                  )
                ),
                filterByOwnerStatus: isNil(values.filterByOwnerStatus)
                  ? WalkingFarmPropertiesOwnerStatusFilterEnum.All
                  : values.filterByOwnerStatus
              }
            })
          );
        }
      });

    if (this.walkingFarmId) {
      this.store
        .pipe(select(fromWalkingFarmSelectors.selectWalkingFarmViewFilters(this.walkingFarmId)))
        .pipe(throttleTime(500, undefined, { leading: true, trailing: true }), distinctUntilChanged())
        .subscribe(({ filterByLeadStatus, filterByOwnerStatus }) => {
          this.filterForm.patchValue(
            {
              filterByLeadStatus: map(walkingFarmAvailableLeadStatuses, (item) =>
                includes(filterByLeadStatus, item.value)
              ),
              filterByOwnerStatus: isNil(filterByOwnerStatus)
                ? WalkingFarmPropertiesOwnerStatusFilterEnum.All
                : filterByOwnerStatus
            },
            { emitEvent: false }
          );
        });
    }

    if (this.walkingFarmId) {
      of(this.walkingFarmId)
        .pipe(
          filter((id) => !isNil(id)),
          concatMap((id) => this.store.pipe(select(fromWalkingFarmSelectors.selectWalkingFarmProperties(id)))),
          filter((walkingFarmProperties) => !isUndefined(walkingFarmProperties))
        )
        .subscribe((properties) => {
          this.properties = properties;
        });
    }
  }

  async clearFilters() {
    if (this.walkingFarmId) {
      this.store.dispatch(
        fromWalkingFarmActions.clearViewFarmFilters({
          payload: {
            farmId: this.walkingFarmId
          }
        })
      );
      await this.tooltipService.dismissAll();
      const toast = await this.toastController.create({
        color: 'light',
        duration: defaultToastDuration,
        message: 'All filters cleared.',
        position: 'top',
        positionAnchor: 'filter-header'
      });
      await toast?.present();
    }
  }

  async sortBy(type: WalkingFarmPropertiesSortByEnum) {
    if (this.walkingFarmId) {
      let result = cloneDeep(this.properties);
      let sortedBy = 'N/A';
      switch (type) {
        case WalkingFarmPropertiesSortByEnum.ParcelNumber:
          result = orderBy(result, ['A037_ParcelNumber'], ['asc']);
          sortedBy = 'parcel number';
          break;
        case WalkingFarmPropertiesSortByEnum.LeadStatus:
          result = sortBy(result, (item) => {
            const status = lowerCase(item.Status);
            return findIndex(
              [
                WalkingFarmLeadStatusEnum.Lead,
                WalkingFarmLeadStatusEnum.Prospect,
                WalkingFarmLeadStatusEnum.NotHome,
                WalkingFarmLeadStatusEnum.NotContacted,
                WalkingFarmLeadStatusEnum.NotInterested,
                WalkingFarmLeadStatusEnum.OnMarket
              ],
              (orderItem) => orderItem === status
            );
          });

          sortedBy = 'lead status';
          break;
        case WalkingFarmPropertiesSortByEnum.StreetName:
          result = orderBy(result, ['A004_StreetName', 'A002_HouseNumber', 'A005_UnitNumber'], ['asc']);
          sortedBy = 'street name';
          break;
        case WalkingFarmPropertiesSortByEnum.UnreadAlerts:
          result = orderBy(result, ['unreadFarmAlerts'], ['desc']);
          sortedBy = 'unread alerts';
          break;
        default:
          break;
      }
      const propertiesOrder = lodashMap(result, (item) => `${item.A000_PropertyNumber}`);
      this.store.dispatch(
        fromWalkingFarmAcions.updatePropertiesOrder({
          payload: {
            farmId: this.walkingFarmId,
            order: propertiesOrder
          }
        })
      );

      const toast = await this.toastController.create({
        buttons: [
          {
            htmlAttributes: {
              'aria-label': 'close'
            },

            icon: 'close'
          }
        ],
        color: 'light',
        duration: defaultToastDuration,
        message: `Properties sorted by ${sortedBy}.`,
        position: 'top',
        positionAnchor: 'filter-header'
      });
      await toast.present();
    }
  }
}
